import { ChevronLeft, Lightbulb, MessageSquare, RefreshCw } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import SemanticSummaryView from "@/components/summary-engine/SemanticSummaryView";
import { Button } from "@/components/ui/Button";
import { useStore } from "@/store";
import { useChatStore } from "@/store/chat/chat.store";
import { apiURL } from "@/utils";
import { useNavigate } from "@tanstack/react-router";
import CategoryChatView from "./CategoryChatView";
import { ChatThreadView } from "./ChatThreadView";
import FilePreview from "./FilePreview";
import MessageInput from "./MessageInput";
import MessageList from "./MessageList";

const GENERAL_SECTION_ID = "00000000-0000-0000-0000-000000000001";

type TChatViewProps = {
  onRefreshSummary?: () => void;
  isRefreshingSummary?: boolean;
  isTaskRunning?: boolean;
  summaryCtaButton?: React.ReactNode;
  view?: "summary" | "conversation";
  onViewChange?: (view: "summary" | "conversation") => void;
};

export default function ChatView({
  onRefreshSummary,
  isRefreshingSummary = false,
  isTaskRunning = false,
  summaryCtaButton,
  view,
  onViewChange,
}: TChatViewProps = {}) {
  const folders = useChatStore((state) => state.folders);
  const activeChatId = useChatStore((state) => state.activeChatId);
  const activeChatThread = useChatStore((state) => state.activeChatThread);
  const createThread = useChatStore((state) => state.createThread);
  const openChatThreadSession = useChatStore((state) => state.openChatThreadSession);
  const setThreadTitle = useChatStore((state) => state.setThreadTitle);
  const navigate = useNavigate();
  const companies = useStore((state) => state.companies);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToSummaryRef = useRef(false);
  const summaryHeadingRef = useRef<HTMLDivElement>(null);
  const [deepDivingIdx, setDeepDivingIdx] = useState<number | null>(null);

  // Compute these before any early returns to ensure hooks are always called in the same order
  const activeChat = folders.flatMap((folder) => folder.chats).find((chat) => chat.id === activeChatId);
  const activeFolder = folders.find((folder) => folder.chats.some((chat) => chat.id === activeChatId));
  const hasSummary = activeChat?.metadata?.summary;

  const handleDeepDive = useCallback(
    async (rec: string, idx: number) => {
      const companyId = typeof activeChat?.metadata?.companyId === "string" ? activeChat.metadata.companyId : null;
      if (!companyId) return;
      setDeepDivingIdx(idx);
      try {
        const result = await createThread({
          company_id: companyId,
          section_id: GENERAL_SECTION_ID,
          title: rec.slice(0, 60),
        });
        if (!result.success || !result.thread) return;
        const thread = result.thread;
        setThreadTitle(thread.id, thread.title ?? rec.slice(0, 60));
        const company = companies?.find((c) => c.id === companyId);
        const section = company?.sections?.find((s) => s.id === GENERAL_SECTION_ID);
        openChatThreadSession({
          threadId: thread.id,
          companyId,
          companyName: company?.name,
          sectionId: thread.section_id ?? GENERAL_SECTION_ID,
          sectionName: section?.name ?? "General",
          title: thread.title ?? rec.slice(0, 60),
          initialPrompt: rec,
        });
        void navigate({
          to: "/app/companies/$company_id/sections/$section_id/threads/$thread_id",
          params: {
            company_id: thread.company_id,
            section_id: thread.section_id ?? GENERAL_SECTION_ID,
            thread_id: thread.id,
          },
        });
      } finally {
        setDeepDivingIdx(null);
      }
    },
    [activeChat?.metadata?.companyId, companies, createThread, openChatThreadSession, setThreadTitle, navigate]
  );

  // Instant scroll to bottom when switching chats (no animation for history)
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
    }
  }, [activeChatId]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (!activeChatThread && activeChat) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [activeChat?.messages, activeChat?.metadata?.summary, activeChatThread, activeChat]);

  // Reset scroll flag when chat changes
  useEffect(() => {
    hasScrolledToSummaryRef.current = false;
  }, [activeChatId]);

  // Scroll to summary heading when summary first appears (for onboarding completion)
  useEffect(() => {
    if (!activeChatThread && hasSummary && !hasScrolledToSummaryRef.current) {
      const scrollTimeout = setTimeout(() => {
        if (summaryHeadingRef.current) {
          summaryHeadingRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          hasScrolledToSummaryRef.current = true;
        }
      }, 100);

      return () => clearTimeout(scrollTimeout);
    }
  }, [hasSummary, activeChatThread, activeChatId]);

  if (activeChatThread) {
    return <ChatThreadView />;
  }

  if (!activeChat) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-muted-foreground">
        <p>Select a chat or start a new one</p>
      </div>
    );
  }

  if (activeFolder?.isCategory) {
    return (
      <CategoryChatView
        title={activeFolder.name === "Investment Readiness" ? "Investor Readiness" : activeFolder.name}
      />
    );
  }

  // Get all files from metadata (including non-image files)
  // Type guard to ensure we have a valid array
  type TFileMetadata = {
    file_id: string;
    file_name?: string | null;
    file_url: string;
    content_type?: string | null;
    file_size?: number | null;
  };

  const isFileMetadataArray = (value: unknown): value is TFileMetadata[] => {
    return Array.isArray(value);
  };

  const allFilesRaw: unknown = activeChat.metadata?.allFiles ?? activeChat.metadata?.imageFiles;
  const allFiles: TFileMetadata[] = isFileMetadataArray(allFilesRaw) ? allFilesRaw : [];

  // Conversation-only mode: show Q&A history with back button header
  if (view === "conversation" && onViewChange) {
    return (
      <div className="relative flex h-full flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-4">
          <div>
            <p className="text-md font-semibold text-foreground">Onboarding conversation</p>
            <p className="text-sm text-muted-foreground">Read-only history</p>
          </div>
          <button
            onClick={() => onViewChange("summary")}
            className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
          >
            <ChevronLeft className="h-4 w-4" />
            Back to summary
          </button>
        </div>
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-y-auto"
          data-tour="chat-messages"
        >
          <MessageList messages={activeChat.messages} />
          <div ref={messagesEndRef} />
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col overflow-hidden">
      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
        data-tour="chat-messages"
      >
        {/* When view is "summary" (or no view toggle), hide messages and show only summary */}
        {view !== "summary" && <MessageList messages={activeChat.messages} />}
        {hasSummary && (
          <div
            ref={summaryHeadingRef}
            className="mx-auto w-full max-w-4xl p-6"
          >
            <div className="mb-4 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                {view === "summary" && onViewChange && (
                  <button
                    onClick={() => onViewChange("conversation")}
                    className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  >
                    <MessageSquare className="h-4 w-4" />
                    View conversation
                  </button>
                )}
                {view !== "summary" && (
                  <>
                    <Lightbulb className="h-5 w-5 text-muted-foreground" />
                    <h2 className="text-lg font-semibold text-foreground">Onboarding Summary</h2>
                  </>
                )}
              </div>
              <div className="flex items-center gap-2">
                {summaryCtaButton}
                {onRefreshSummary && (
                  <Button
                    onClick={() => onRefreshSummary()}
                    disabled={isRefreshingSummary || isTaskRunning}
                    variant="outline"
                    size="sm"
                    className="flex items-center gap-2"
                    title="Refresh onboarding summary"
                  >
                    <RefreshCw className={`h-4 w-4 ${isRefreshingSummary ? "animate-spin" : ""}`} />
                    {isRefreshingSummary ? "Refreshing..." : "Refresh"}
                  </Button>
                )}
              </div>
            </div>
            {(() => {
              const rawSummary = activeChat.metadata?.rawSummary ?? activeChat.metadata?.summary ?? "";
              const companyName =
                typeof activeChat.metadata?.companyName === "string" ? activeChat.metadata.companyName : null;
              const companyRole =
                typeof activeChat.metadata?.companyRole === "string" ? activeChat.metadata.companyRole : null;

              return (
                <SemanticSummaryView
                  raw={rawSummary}
                  companyName={companyName}
                  companyRole={companyRole}
                  onDeepDive={(rec, idx) => void handleDeepDive(rec, idx)}
                  deepDivingIndex={deepDivingIdx}
                />
              );
            })()}
            {allFiles.length > 0 && (
              <div className="mt-6">
                <h3 className="mb-3 text-base font-semibold text-foreground">Uploaded Files</h3>
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                  {allFiles.map((file) => {
                    const fileId = file.file_id;
                    const rawUrl = file.file_url;
                    const fileUrl = rawUrl?.startsWith("/") ? apiURL(rawUrl).toString() : rawUrl;
                    const fileName = file.file_name ?? "Uploaded file";
                    const contentType = file.content_type ?? null;
                    const fileSize = file.file_size ?? null;

                    if (!fileId || !fileUrl) {
                      return null;
                    }

                    return (
                      <FilePreview
                        key={fileId}
                        fileId={fileId}
                        fileName={fileName}
                        fileUrl={fileUrl}
                        contentType={contentType}
                        fileSize={fileSize}
                        variant="thumbnail"
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      {!view && (
        <div data-tour="chat-input">
          <MessageInput />
        </div>
      )}
    </div>
  );
}
